import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RENDERER } from "../../constants/QueryKeys";
import { RendererType } from "../../constants/RendererConstants.mjs";
import SiteService from "../../service/SiteService.mjs";
import { STATIC_QUERY_DEFAULTS } from "../../utils/QueryUtils";

/**
 * @typedef {import("../../constants/RendererConstants.mjs").RendererTypeValue} RendererTypeValue
 */

const defaults = STATIC_QUERY_DEFAULTS;

/**
 * @callback SetProtorenderer
 * @param {boolean} on
 * @returns {Promise<{ renderer: RendererTypeValue, canToggle: boolean }>}
 *
 * @typedef {Object} UseRendererResult
 * @prop {boolean} useProtorenderer - false until the first load resolves
 * @prop {boolean} canToggle - false while loading, or once past the v2 cutoff
 * @prop {boolean} isSaving
 * @prop {SetProtorenderer} setProtorenderer
 * @prop {boolean} settled - has the initial request settled?
 */

/**
 * Reads + persists the publish renderer opt-in (site_designs.renderer) for a design.
 *
 * @param {number} siteId
 * @param {{ enabled?: boolean }} [options] - gate the read (e.g. only when the toggle is visible)
 * @returns {UseRendererResult}
 */
const useRenderer = (siteId, { enabled = true } = {}) => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: [RENDERER, siteId],
    queryFn: () => SiteService.getRenderer(siteId),
    enabled: enabled && siteId != null,
    ...defaults,
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (renderer) => SiteService.updateRenderer(siteId, renderer),
    onSuccess: (res) => queryClient.setQueryData([RENDERER, siteId], res),
  });

  return {
    useProtorenderer: data?.renderer === RendererType.V2,
    canToggle: data?.canToggle ?? false,
    isSaving: isPending,
    setProtorenderer: (on) => mutateAsync(on ? RendererType.V2 : RendererType.V1),
    settled: data?.renderer != null,
  };
};

export default useRenderer;
